import { useParams } from 'react-router-dom'
import { submitMilestoneStep } from '../../../services/api/collaboration'
import { Gig, Milestone, MilestoneStep } from '../../../types/collaborations/CollaborationTypes'
import withSlideUpModal from '../../../utils/hoc/withSlideUpModal'
import React, { useEffect, useMemo, useState } from 'react'

import CampaignGigs from './gigs/CampaignGigs'
import { toast } from 'react-toastify'
import NearbyStores from './gigs/store-visit/NearbyStores'
import { Address } from '../../../types/address'
import { fetchPlace } from '../../../services/api/address'

interface GigAdditionProps {
    gigs: Gig[]
    activeMilestone: Milestone
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    onPlayButtonClick: (gig: Gig) => void,
    handleDownload: (gig: Gig) => void,
    step: MilestoneStep
}

const GigAddition: React.FC<GigAdditionProps> = ({ activeMilestone, gigs, step, onPlayButtonClick, handleDownload, onClose }) => {

    const { campaignId } = useParams();

    const [selectedGigs, setSelectedGigs] = useState<number[]>([]);
    const [places, setPlaces] = useState<Record<string, any>>({});
    const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
    const [address, setAddress] = useState<Address | null>(null);
    const [name, setName] = useState('');

    const [submitting, setSubmitting] = useState(false);


    const getCity = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('administrative_area_level_3'));
        return component?.longText ?? ''
    }

    const getRegion = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('administrative_area_level_1'));
        return component?.longText ?? '';
    }

    const getPincode = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('postal_code'));
        return component?.longText ?? '';
    }

    const getSublocality = (place: any) => {
        const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
        const component = addressComponents.find((component: any) => component.types?.includes('sublocality'));
        return component?.longText ?? '';
    }


    const fetchSelectedPlace = async (placeId: string) => {
        try {
            const place = await fetchPlace(placeId);
            setPlaces((prev) => ({ ...prev, [placeId]: place }))

        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (selectedPlaceId && places[selectedPlaceId]) {
            const place = places[selectedPlaceId];
            setName(place.displayName?.text ?? '');
            setAddress({
                name: '',
                city: getCity(place),
                region: getRegion(place),
                sublocality: getSublocality(place),
                pincode: getPincode(place),
                premise: '',
                lat: place.location.latitude,
                lng: place.location.longitude,
                place_id: selectedPlaceId
            })
        }else if(selectedPlaceId){
            fetchSelectedPlace(selectedPlaceId);
        }
    }, [selectedPlaceId, places])

    const onSubmit = async () => {
        if (!campaignId || !name || !address) return;
        try {
            setSubmitting(true);
            const properties = {
                "properties[address][name]": name,
                "properties[address][city]": address.city,
                "properties[address][region]": address.region,
                "properties[address][sublocality]": address.sublocality,
                "properties[address][pincode]": address.pincode,
                "properties[address][lat]": address.lat,
                "properties[address][lng]": address.lng,
                "properties[address][place_id]": address.place_id,
            }

            await submitMilestoneStep(campaignId, { step: step.gig_step.slug, name, ...properties });
            onClose(true);
        } catch (error: any) {
            console.log(error);
            toast.error(error?.response?.data?.message ?? "Unknown error occurred.")
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <React.Fragment>
            {
                activeMilestone?.gig_type?.slug === 'store_visit' &&
                <NearbyStores
                    query={activeMilestone?.specifications?.store_name ?? ''}
                    onChange={(placeId) => {
                        setAddress(null);
                        setName('');
                        setSelectedPlaceId(placeId);
                    }}
                />
            }
            <div className="milestone-step-actions">
                <button className="btn btn-outline-primary fw-600 fs-12" onClick={() => onClose()}>Cancel</button>
                <button className="btn btn-primary fw-600 fs-12" disabled={!name || !address || submitting} onClick={onSubmit}>Submit</button>
            </div>
        </React.Fragment>

    )
}

const GigAdditionModal: React.FC<GigAdditionProps> = (props) => {
    const { activeMilestone } = props
    const title = `Find Nearby ${activeMilestone.specifications?.store_name || 'Store'}`

    const ModalComponent = useMemo(() => withSlideUpModal(GigAddition, title), [title])
    return <ModalComponent {...props} />
}


export default GigAdditionModal;